import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { vendorAPI } from '../../../api/vendor';
import {
    Package, RotateCcw, AlertCircle, Clock, Search, ChevronRight,
} from 'lucide-react';
import { formatDate, formatDateTimeEnglish } from '../../../utils/date';
import { formatPrice } from '../../../utils/utilityFunctions';
import toast from '../../../utils/toast';
import type { ReturnRequest } from '../../../types';

type FilterKey = 'pending' | 'approved' | 'rejected' | 'all';

const STATUS_META: Record<ReturnRequest['status'], { label: string; classes: string }> = {
    pending:            { label: 'Pending',   classes: 'bg-amber-100 text-amber-800' },
    approved:           { label: 'Approved',  classes: 'bg-blue-100 text-blue-800' },
    partially_approved: { label: 'Partial',   classes: 'bg-blue-100 text-blue-800' },
    auto_approved:      { label: 'Auto-approved', classes: 'bg-blue-100 text-blue-800' },
    rejected:           { label: 'Rejected',  classes: 'bg-red-100 text-red-800' },
    cancelled:          { label: 'Cancelled', classes: 'bg-gray-100 text-gray-700' },
    in_transit:         { label: 'In transit', classes: 'bg-indigo-100 text-indigo-800' },
    completed:          { label: 'Completed', classes: 'bg-green-100 text-green-800' },
    failed:             { label: 'Failed',    classes: 'bg-red-100 text-red-800' },
};

const FILTER_BUCKETS: Record<FilterKey, string[]> = {
    pending: ['pending'],
    approved: ['approved', 'partially_approved', 'auto_approved', 'in_transit', 'completed'],
    rejected: ['rejected', 'failed', 'cancelled'],
    all: [],
};

const VendorReturns: React.FC = () => {
    const navigate = useNavigate();
    const [items, setItems] = useState<ReturnRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<FilterKey>('pending');
    const [search, setSearch] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const data = await vendorAPI.getReturns();
                setItems(Array.isArray(data) ? data : data.results ?? []);
            } catch {
                toast.error('Failed to load returns');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const counts = useMemo(() => {
        const c: Record<FilterKey, number> = { pending: 0, approved: 0, rejected: 0, all: items.length };
        for (const r of items) {
            if (FILTER_BUCKETS.pending.includes(r.status)) c.pending++;
            else if (FILTER_BUCKETS.approved.includes(r.status)) c.approved++;
            else c.rejected++;
        }
        return c;
    }, [items]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return items.filter(r => {
            if (filter !== 'all' && !FILTER_BUCKETS[filter].includes(r.status)) return false;
            if (!q) return true;
            return (
                r.product_name.toLowerCase().includes(q) ||
                r.order_number.toLowerCase().includes(q) ||
                (r.customer_email || '').toLowerCase().includes(q)
            );
        });
    }, [items, filter, search]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    return (
        <div className="py-6 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Returns</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Review and respond to customer return requests.
                    </p>
                </div>

                {/* Filters + search */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                    <div className="flex gap-1 overflow-x-auto">
                        {(['pending', 'approved', 'rejected', 'all'] as FilterKey[]).map(k => (
                            <button
                                key={k}
                                onClick={() => setFilter(k)}
                                className={`px-3 py-1.5 text-sm rounded-lg whitespace-nowrap font-medium ${
                                    filter === k
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                {k === 'pending' ? 'Pending' :
                                 k === 'approved' ? 'Approved' :
                                 k === 'rejected' ? 'Rejected' : 'All'}
                                <span className={`ml-1.5 text-xs ${filter === k ? 'text-blue-100' : 'text-gray-500'}`}>
                                    {counts[k]}
                                </span>
                            </button>
                        ))}
                    </div>
                    <div className="relative flex-1 sm:max-w-xs sm:ml-auto">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search by order, product, customer"
                            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                </div>

                {filtered.length === 0 ? (
                    <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                        <RotateCcw className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <h2 className="text-lg font-semibold text-gray-900">
                            {search ? 'No matches' : filter === 'pending' ? 'All caught up' : 'Nothing here yet'}
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                            {search
                                ? 'Try a different search term.'
                                : filter === 'pending'
                                    ? 'No return requests are awaiting your decision.'
                                    : 'No returns in this category.'}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {filtered.map(r => {
                            const meta = STATUS_META[r.status];
                            const isPending = r.status === 'pending';
                            return (
                                <Link
                                    key={r.id}
                                    to={`/vendor/returns/${r.id}`}
                                    className={`flex items-center gap-4 p-4 bg-white border rounded-xl hover:border-blue-300 transition-colors ${
                                        isPending ? 'border-amber-200' : 'border-gray-200'
                                    }`}
                                >
                                    <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center">
                                        {r.product_image ? (
                                            <img src={r.product_image} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <Package className="w-6 h-6 text-gray-400" />
                                        )}
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start gap-2">
                                            <div className="min-w-0 flex-1">
                                                <p className="font-medium text-gray-900 truncate">{r.product_name}</p>
                                                <p className="text-xs text-gray-500 mt-0.5 truncate">
                                                    #{r.order_number} · {r.customer_email}
                                                </p>
                                            </div>
                                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${meta.classes}`}>
                                                {meta.label}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                                            <span className="capitalize">{r.kind}</span>
                                            <span>·</span>
                                            <span>Qty {r.requested_quantity}</span>
                                            <span>·</span>
                                            <span className="font-medium text-gray-700">
                                                {formatPrice(r.item_refund_amount)}
                                            </span>
                                            {isPending && (
                                                <>
                                                    <span>·</span>
                                                    <span className="text-amber-600 flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        due {formatDateTimeEnglish(r.sla_deadline)}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                </Link>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default VendorReturns;